import AddIcon from '@mui/icons-material/Add';
import { CircularProgress, FormControl, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateRange } from '@mui/x-date-pickers-pro';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom';
import { compose } from 'redux';
import { getAuthPersonSelector } from '@redux/oauth/oauthSelectors';
import { getActiveOrganizationIdSelector } from '@redux/organization/organizationSelectors';
import { getActivePersonIdNamePairByPersonIdSelector, getPersonByIdSelector } from '@redux/person/personSelector';
import { getAllActivePersonThunk } from '@redux/person/personThunks';
import { PersonIdNameTimezone } from '@redux/person/personTypes';
import { tasksByDateRangeLoadingSelector, tasksByDateRangeSelector } from '@redux/tasks/tasksSelectors';
import { getPersonTasksByDateRangeThunk, postNewTaskThunk } from '@redux/tasks/tasksThunks';
import { TaskCreationPayloadType, TaskSummaryType, PatientEngagementPlan } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { Button } from 'components/Button/Button';
import CaregiverAssistantDrawer from 'components/CaregiverAssistantDrawer/CaregiverAssistantDrawer';
import { EmptyListArea } from 'components/EmptyListArea/EmptyListArea';
import { ListChecksIcon, SparkleIcon } from 'components/Icons';
import { Text } from 'components/Typography';
import { PatientTask } from 'containers/PatientDashboard/components/PatientTask';
import CancelTaskModal from 'containers/Tasks/components/CancelTaskModal/CancelTaskModal';
import TaskCreationDrawer from 'containers/Tasks/components/TaskCreationDrawer/TaskCreationDrawer';
import TaskInfoDrawer from 'containers/Tasks/components/TaskInfoDrawer/TaskInfoDrawer';
import { groupTasks } from 'utils/taskUtils';
import { parseDateStringIgnoreTimezone } from 'utils/time';
import { makeCustomSelectStyles } from '../../../../customStyles';
import style from './PatientTaskTable.scss';

interface PatientTaskTableProps {
  patientEngagementPlans: PatientEngagementPlan[];
  onRefreshData?: () => void;
  hasDeviceSetup?: boolean;
}

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux & PatientTaskTableProps;

enum TASK_TABS {
  ALL = 'All',
  SCHEDULED = 'Scheduled',
  PAST = 'Past'
}

export const PatientTaskTable: React.FC<Props> = ({
  postNewTask,
  getPersonTasksByDateRange,
  activePersonByIdSelector,
  personById,
  tasks,
  isLoading,
  patientEngagementPlans,
  getAllActivePerson,
  authPersonSelector,
  activeOrganizationId,
  onRefreshData,
  hasDeviceSetup = true
}) => {
  const { userId: personId } = useParams<{ userId: string }>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [caregiverAssistantOpen, setCaregiverAssistantOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [activePersonList, setActivePersonList] = useState<PersonIdNameTimezone[]>([]);
  const [cancelTaskOptions, setCancelTaskOptions] = useState<{ id: string; isSingleCancel: boolean } | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const today = new Date();
  const [expandedTasks, setExpandedTasks] = useState<string[]>([]);
  const [hasUserSetDateRange, setHasUserSetDateRange] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  const flags = useFlags();
  const showCaregiverFeature = flags.webAppCaregiverAssistant ?? false;

  const { showAlertInfo, showAlertWarning } = useAlert();
  const customSelectStyles = makeCustomSelectStyles();

  // Calculate intelligent default date range based on engagement plans
  const calculateDefaultDateRange = useCallback((): [Date, Date] => {
    const activePlans = (patientEngagementPlans || []).filter((plan) => plan.status === 'ACTIVE');

    // Find earliest start date and latest end date from active engagement plans
    let earliestStart: Date | null = null;
    let latestEnd: Date | null = null;

    activePlans.forEach((plan) => {
      if (plan.startDate) {
        const startDate = parseDateStringIgnoreTimezone(plan.startDate);
        if (!startDate) return; // Skip invalid dates
        if (!earliestStart || startDate < earliestStart) {
          earliestStart = startDate;
        }

        // Calculate end date from start date + duration
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + plan.durationDays);
        if (!latestEnd || endDate > latestEnd) {
          latestEnd = endDate;
        }
      }
    });

    // If we found dates, use them; otherwise fall back to default range
    if (earliestStart && latestEnd) {
      return [earliestStart, latestEnd];
    } else {
      // Fallback to default range if no valid dates found
      return [new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7), new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7)];
    }
  }, [patientEngagementPlans, today]);

  const [defaultStart, defaultEnd] = calculateDefaultDateRange();
  const [selectedDateStart, setSelectedDateStart] = useState<Date>(defaultStart);
  const [selectedDateEnd, setSelectedDateEnd] = useState<Date>(defaultEnd);
  const [dateRange, setDateRange] = useState<DateRange<Date>>([selectedDateStart, selectedDateEnd]);

  const toggleExpand = (taskId: string) => {
    setExpandedTasks((prev) => (prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]));
  };

  useEffect(() => {
    if (personId) {
      getPersonTasksByDateRange(personId, selectedDateStart, selectedDateEnd, selectedPlanId);
    }
  }, [personId, selectedDateStart, selectedDateEnd, selectedPlanId, getPersonTasksByDateRange]);

  // Load active persons data
  useEffect(() => {
    if (activeOrganizationId) {
      getAllActivePerson(activeOrganizationId);
    }
  }, [activeOrganizationId, getAllActivePerson]);

  // Reset user date range flag when personId changes (new patient)
  useEffect(() => {
    setHasUserSetDateRange(false);
  }, [personId]);

  // Update date range when engagement plans change, but only if user hasn't manually set a date range
  useEffect(() => {
    if (!hasUserSetDateRange) {
      const [newStart, newEnd] = calculateDefaultDateRange();
      if (newStart.getTime() !== selectedDateStart.getTime() || newEnd.getTime() !== selectedDateEnd.getTime()) {
        setSelectedDateStart(newStart);
        setSelectedDateEnd(newEnd);
        setDateRange([newStart, newEnd]);
      }
    }
  }, [patientEngagementPlans, calculateDefaultDateRange, selectedDateStart, selectedDateEnd, hasUserSetDateRange]);

  useEffect(() => {
    const getActivePersons = (): PersonIdNameTimezone[] => {
      const activePersons: PersonIdNameTimezone[] = [];
      if (!activePersonByIdSelector) {
        return activePersons;
      }
      for (const id of Object.keys(activePersonByIdSelector)) {
        if (id !== personId) {
          continue;
        }
        const personIdNamePair = activePersonByIdSelector[id];
        if (!personIdNamePair) {
          continue;
        }

        // Get the full person data to ensure we have complete name information
        const fullPersonData = personById[id];
        const fullName =
          fullPersonData?.givenNames && fullPersonData?.familyName ? `${fullPersonData.givenNames} ${fullPersonData.familyName}`.trim() : personIdNamePair.fullName || '';

        const activePerson: PersonIdNameTimezone = {
          ...personIdNamePair,
          fullName: fullName
        };
        activePersons.push(activePerson);
      }

      return activePersons;
    };

    setActivePersonList(getActivePersons());
  }, [activePersonByIdSelector, personById, personId]);

  const handleSubmitAddNewTask = useCallback(
    async (task: TaskCreationPayloadType) => {
      try {
        await postNewTask(task);
        showAlertInfo({
          message: 'Task created successfully!'
        });
        setIsDrawerOpen(false);
      } catch (err) {
        showAlertWarning({
          message: 'Something went wrong creating your task. Please try again.'
        });
      }
    },
    [postNewTask, showAlertInfo, showAlertWarning]
  );

  const scheduledTasks = useMemo(
    () =>
      (tasks || []).filter(
        (summary) => (summary.taskStatus === 'SCHEDULED' && !summary.recurringTasksList) || (summary.recurringTasksList || []).some((task) => task.taskStatus === 'SCHEDULED')
      ),
    [tasks]
  );

  const pastTasks = useMemo(
    () =>
      (tasks || []).filter(
        (summary) =>
          (['EXPIRED', 'CANCELED', 'COMPLETED'].includes(summary.taskStatus) && !summary.recurringTasksList) ||
          (summary.recurringTasksList || []).some((task) => ['EXPIRED', 'CANCELED', 'COMPLETED'].includes(task.taskStatus))
      ),
    [tasks]
  );

  const onSelectTaskSwitch = useCallback((id: string = '') => {
    setSelectedTaskId(id);
  }, []);

  const showConfirmationModal = useCallback((taskId: string = '', isSingleCancel: boolean) => {
    setCancelTaskOptions({ id: taskId, isSingleCancel });
  }, []);

  const hideConfirmationModal = useCallback(() => {
    setCancelTaskOptions(null);
  }, []);

  const handleCancelSuccess = useCallback(() => {
    // Close the task info drawer by clearing selected task
    setSelectedTaskId('');
    // Refresh tasks data
    if (personId) {
      getPersonTasksByDateRange(personId, selectedDateStart, selectedDateEnd, selectedPlanId);
    }
    // Refresh engagement plans data
    onRefreshData?.();
  }, [personId, selectedDateStart, selectedDateEnd, selectedPlanId, getPersonTasksByDateRange, onRefreshData]);

  const renderTable = (tasksToRender: TaskSummaryType[], activeFilter: TASK_TABS) => (
    <TableContainer>
      <Table className={style.patientsTable}>
        <TableHead>
          <TableRow className={style.tableRow}>
            <TableCell className={classNames(style.nameColumn, style.tableCell)}>Name</TableCell>
            <TableCell className={classNames(style.completedColumn, style.tableCell)}>Tasks Finished</TableCell>
            <TableCell className={classNames(style.dateColumn, style.tableCell)} align="right">
              Scheduled for
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groupTasks(tasksToRender).map((groupedTask) => {
            const { parent, children } = groupedTask;
            // Use taskId for unique expansion tracking instead of taskName
            const isExpanded = expandedTasks.includes(parent.taskId);

            // Use task summary or name from first child if parent is missing both
            const taskWithName = {
              ...parent,
              taskName: parent.taskName || (parent.recurringTasksList && parent.recurringTasksList[0]?.taskName) || 'Unknown Task',
              taskSummary: parent.taskSummary || (parent.recurringTasksList && parent.recurringTasksList[0]?.taskSummary)
            };

            let filteredChildren = parent.recurringTasksList || [];
            if (activeFilter === TASK_TABS.SCHEDULED) {
              filteredChildren = (parent.recurringTasksList || []).filter((c) => c.taskStatus === 'SCHEDULED');
            } else if (activeFilter === TASK_TABS.PAST) {
              filteredChildren = (parent.recurringTasksList || []).filter((c) => ['EXPIRED', 'CANCELED', 'COMPLETED'].includes(c.taskStatus));
            }

            return (
              <PatientTask
                key={parent.taskId}
                task={taskWithName}
                onSelectTaskSwitch={onSelectTaskSwitch}
                onCancelTask={showConfirmationModal}
                isExpanded={isExpanded}
                childTasks={filteredChildren}
                onToggleExpand={() => toggleExpand(parent.taskId)}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const tabs = useMemo(
    () => [
      {
        label: `${TASK_TABS.ALL} (${(tasks || []).length})`,
        content: renderTable(tasks || [], TASK_TABS.ALL)
      },
      {
        label: `${TASK_TABS.SCHEDULED} (${scheduledTasks.length})`,
        content: renderTable(scheduledTasks, TASK_TABS.SCHEDULED)
      },
      {
        label: `${TASK_TABS.PAST} (${pastTasks.length})`,
        content: renderTable(pastTasks, TASK_TABS.PAST)
      }
    ],
    [tasks, scheduledTasks, pastTasks, expandedTasks]
  );

  const handleCloseCaregiverAssistantDrawer = useCallback(() => {
    setCaregiverAssistantOpen(false);
  }, []);

  const renderAddTaskButton = () => (
    <Tooltip title={hasDeviceSetup ? '' : "The patient's device hasn't been set up yet"} followCursor placement="top">
      <span aria-label={hasDeviceSetup ? undefined : 'Button disabled: patient device setup required'}>
        <Button variant="contained" onClick={() => hasDeviceSetup && setIsDrawerOpen(true)} startIcon={<AddIcon />} disabled={!hasDeviceSetup}>
          Add Task
        </Button>
      </span>
    </Tooltip>
  );

  const renderCaregiverButton = () => (
    <Button variant="outlined" disabled={!hasDeviceSetup} onClick={() => setCaregiverAssistantOpen(true)} startIcon={<SparkleIcon height={16} width={16} />}>
      Caregiver Assistant
    </Button>
  );

  const renderPlanSelector = () => {
    // Group patient engagement plans by engagement_plan_id to avoid duplicates
    // Keep only the first occurrence of each engagementPlanId
    const seenEngagementPlanIds = new Set<string>();
    const uniquePlans = (patientEngagementPlans || []).filter((plan) => {
      if (seenEngagementPlanIds.has(plan.engagementPlanId)) {
        return false;
      }
      seenEngagementPlanIds.add(plan.engagementPlanId);
      return true;
    });

    return (
      <FormControl variant="outlined" size="small">
        <Select
          value={selectedPlanId || 'all'}
          onChange={(e) => setSelectedPlanId(e.target.value === 'all' ? null : (e.target.value as string))}
          className={customSelectStyles.select}
          sx={{
            minWidth: '150px',
            maxWidth: '350px',
            '& .MuiOutlinedInput-input': {
              height: '24px !important',
              lineHeight: '24px !important'
            },
            '& .MuiSelect-outlined.MuiSelect-outlined': {
              padding: '10px 32px 10px 14px !important'
            }
          }}
        >
          <MenuItem value="all">All plans</MenuItem>
          {uniquePlans.map((plan) => (
            <MenuItem key={plan.engagementPlanId} value={plan.engagementPlanId}>
              {plan.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderDateRangePicker = () => (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateRangePicker
        slots={{ field: SingleInputDateRangeField }}
        value={dateRange}
        onChange={(newValue) => setDateRange(newValue)}
        onAccept={(newValue) => {
          if (!newValue[0] || !newValue[1]) return;
          setSelectedDateStart(newValue[0]);
          setSelectedDateEnd(newValue[1]);
          setHasUserSetDateRange(true);
        }}
        closeOnSelect={false}
        slotProps={{
          actionBar: {
            actions: ['accept', 'cancel']
          },
          textField: {
            sx: {
              '& .MuiOutlinedInput-root': { paddingRight: 0, borderRadius: '8px' },
              input: { width: '230px', boxSizing: 'border-box', padding: '10px 14px', lineHeight: '24px' }
            }
          }
        }}
      />
    </LocalizationProvider>
  );

  if (isLoading) {
    return (
      <div className={style.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {!isLoading && (tasks || []).length === 0 ? (
        <div>
          <div className={style.headerRow}>
            <Text variant="xl">Tasks</Text>
            <div>
              {showCaregiverFeature && renderCaregiverButton()}
              {renderAddTaskButton()}
            </div>
          </div>
          <div className={style.filtersContainer}>
            {renderPlanSelector()}
            {renderDateRangePicker()}
          </div>
          <EmptyListArea
            title="No tasks yet"
            description="Start by creating a task to support the patient's care plan."
            icon={ListChecksIcon}
            isButtonVisible={false}
            isSmallVersion
          />
        </div>
      ) : (
        <div className={style.tableWrapper}>
          <div className={style.headerRow}>
            <Text variant="xl">Tasks</Text>
            <div>
              {showCaregiverFeature && renderCaregiverButton()}
              {renderAddTaskButton()}
            </div>
          </div>
          <div className={style.tabsContainer}>
            <div className={style.tabsRow}>
              <div className={style.tabs}>
                {tabs.map((tab, index) => (
                  <button
                    key={index}
                    className={classNames(style.tab, activeTab === index ? style.active : {})}
                    onClick={() => setActiveTab(index)}
                    data-testid={`task-tab-${index}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className={style.filtersContainer}>
                {renderPlanSelector()}
                {renderDateRangePicker()}
              </div>
            </div>
            <div className={style.tabPanelContainer}>{tabs[activeTab].content}</div>
          </div>
        </div>
      )}
      <TaskCreationDrawer
        preselectedPerson={personId}
        visible={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        completeSubmitNewTask={handleSubmitAddNewTask}
        activePersons={activePersonList}
        onSuccessSubmit={() => {
          personId && getPersonTasksByDateRange(personId, selectedDateStart, selectedDateEnd, selectedPlanId);
        }}
      />

      <CaregiverAssistantDrawer
        open={caregiverAssistantOpen}
        onClose={handleCloseCaregiverAssistantDrawer}
        onSuccessSubmit={() => {
          personId && getPersonTasksByDateRange(personId, selectedDateStart, selectedDateEnd, selectedPlanId);
        }}
      />

      <CancelTaskModal
        taskId={cancelTaskOptions?.id}
        visible={!!cancelTaskOptions?.id}
        isSingleCancel={cancelTaskOptions?.isSingleCancel}
        onConfirmPress={onSelectTaskSwitch}
        onClosePress={hideConfirmationModal}
        onSuccess={handleCancelSuccess}
      />
      {selectedTaskId && <TaskInfoDrawer taskId={selectedTaskId} onClose={() => onSelectTaskSwitch()} onCancelTask={showConfirmationModal} />}
    </>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    activePersonByIdSelector: getActivePersonIdNamePairByPersonIdSelector(state),
    personById: getPersonByIdSelector(state),
    authPersonSelector: getAuthPersonSelector(state),
    activeOrganizationId: getActiveOrganizationIdSelector(state),
    tasks: tasksByDateRangeSelector(state),
    isLoading: tasksByDateRangeLoadingSelector(state)
  }),
  {
    postNewTask: postNewTaskThunk,
    getPersonTasksByDateRange: getPersonTasksByDateRangeThunk,
    getAllActivePerson: getAllActivePersonThunk
  }
);

export default compose(connectRedux)(PatientTaskTable);
